import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import useTranslator from "../../hooks/useTranslator";
import ApplicationContext from "../../context/application/ApplicationContext";
import AuthContext from "../../context/auth/AuthContext";
import SkorProtectSelector from "../../components/SkorProtectSelector/SkorProtectSelector";
import CreateGroupScreen from "../../components/CreateGroupScreen/CreateGroupScreen";
import UserIcon from "../../assets/img/user-icon.svg";
import { toast } from "../../helper/common";
import { JoinGroupScreen } from "../../components/InvitationShareModal";
import SkorDefaultLoader from "../../assets/img/skorDefaultLoader.gif";

/* ── Choice screen ─────────────────────────────────────────── */
const ChoiceScreen = ({ onAlone, onGroup, _t, onClose }) => (
  <div className="sos-modal">
    <div className="isf-header">
      <button className="isf-close-btn" onClick={onClose} aria-label="close">
        ✕
      </button>
      <span className="sos-almost">{_t("almostThere")}</span>
      <h2 className="sos-title">{_t("wouldYouBeApplyingWithSomeone")}</h2>
    </div>

    <div className="sos-options">
      <button className="sos-option-card" onClick={onAlone}>
        <div className="sos-option-icon">
          <img src={UserIcon} alt="" />
        </div>
        <div className="sos-option-text">
          <span className="sos-option-title">{_t("noImLookingAlone")}</span>
          <span className="sos-option-sub">
            {_t("shareOnlyYourOwnProfile")}
          </span>
        </div>
        <svg
          className="sos-chevron"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M9 18l6-6-6-6" />
        </svg>
      </button>

      <button className="sos-option-card" onClick={onGroup}>
        <div className="sos-option-icon">
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
        </div>
        <div className="sos-option-text">
          <span className="sos-option-title">{_t("yesInviteFlatmates")}</span>
          <span className="sos-option-sub">
            {_t("createAJointApplication")}
          </span>
        </div>
        <svg
          className="sos-chevron"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M9 18l6-6-6-6" />
        </svg>
      </button>

      <p className="sos-footer-note">{_t("youCanAlwaysChangeLater")}</p>
    </div>
  </div>
);

/* ── Alone / Skor Protect screen ────────────────────────────── */
const AloneScreen = ({ onBack, onConfirm, _t, redirectLoading }) => {
  const [skorProtect, setSkorProtect] = useState(true);

  return (
    <div className="sos-modal sos-modal-sub">
      <div className="sos-sub-header">
        <div className="sos-sub-title-block">
          <h2 className="sos-sub-title">{_t("boostYourProfile")}</h2>
          <span className="sos-sub-subtitle">
            {_t("mostLandlordsPreferSkorProtect")}
          </span>
        </div>
      </div>

      <div className="sos-body">
        <SkorProtectSelector value={skorProtect} onChange={setSkorProtect} />
      </div>

      <div className="sos-footer">
        <button className="isf-btn isf-btn-outline" onClick={onBack}>
          {_t("back")}
        </button>
        <button
          className="btnNext isf-btn isf-btn-primary"
          disabled={redirectLoading}
          onClick={() => onConfirm(skorProtect)}
        >
          {_t("goToMyProfile")}
        </button>
      </div>
    </div>
  );
};

/* ── Main SkorOnboardingStep ────────────────────────────────── */
const SkorOnboardingStep = ({ updateTenantData }) => {
  const { _t } = useTranslator();
  const navigate = useNavigate();
  const {
    createApplication,
    consentApplication,
    pendingApplications,
    loadApplicationsData,
    applicationsLoaded,
  } = useContext(ApplicationContext);
  const { user } = useContext(AuthContext);
  const [subStep, setSubStep] = useState("choice");
  const [selectedApp, setSelectedApp] = useState(null);
  const [redirectLoading, setRedirectLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const currentLang = localStorage.getItem("i18nextLng").toLowerCase() || "en";

  const defaultTitle =
    currentLang === "es"
      ? `Aplicación de ${user?.fname}`
      : `${user?.fname}'s Application`;

  useEffect(() => {
    loadApplicationsData();
  }, []);

  // Show JoinGroupScreen first if user has a pending flatmate invitation
  useEffect(() => {
    const latest = [...(pendingApplications ?? [])].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );
    if (latest.length > 0) {
      setSelectedApp(latest[0]);
      setSubStep("join-group");
    }
  }, [pendingApplications]);

  const navigateUserToHomepage = () => {
    navigate("/home?welcome=true");
  };

  const handleAcceptGroup = async () => {
    if (!selectedApp) return;
    try {
      setProcessing(true);
      await consentApplication(selectedApp._id, "accepted");
      const payload = {
        is_edit: false,
        profile_completed: true,
        role: localStorage.getItem("usertype"),
      };
      await updateTenantData(payload);

      navigateUserToHomepage();
    } catch {
      toast.error(_t("failedToJoinGroup", "Failed to join group"));
    } finally {
      setProcessing(false);
    }
  };

  const handleDeclineGroup = async () => {
    if (!selectedApp) {
      setSubStep("choice");
      setSelectedApp(null);
      return;
    }
    try {
      setProcessing(true);
      await consentApplication(selectedApp._id, "rejected");
    } catch {
      toast.error(_t("failedToDecline", "Failed to decline"));
    } finally {
      setProcessing(false);
    }
    setSubStep("choice");
    setSelectedApp(null);
  };

  const handleCreateGroup = async (participants, skorProtect) => {
    try {
      const payload = {
        is_edit: false,
        profile_completed: true,
        role: localStorage.getItem("usertype"),
      };
      await updateTenantData(payload);

      await createApplication({
        participants,
        title: defaultTitle,
        skor_protect: skorProtect,
      });

      navigateUserToHomepage();
    } catch (err) {
      toast.error(err?.response?.data?.message ?? _t("failedToCreateGroup"));
    }
  };

  const saveInvividualPreferenceForSkorProtect = async (
    skor_protect_value = false
  ) => {
    try {
      setRedirectLoading(true);
      const payload = {
        skor_protect: skor_protect_value,
        is_edit: false,
        profile_completed: true,
        role: localStorage.getItem("usertype"),
      };
      await updateTenantData(payload);

      navigateUserToHomepage();
    } catch (err) {
      toast.error(err?.response?.data?.message ?? _t("failedToRedirect"));
    } finally {
      setRedirectLoading(false);
    }
  };

  if (!applicationsLoaded) {
    return (
      <div className="wrapper-modal isf-overlay modal-fixed-screen">
        <div className="isf-container">
          <div className="isf-loading">
            <img src={SkorDefaultLoader} className="defaultSkorLoader" alt="" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="newmodalui asguarantor wrapper-modal addContextModal modal-fixed-screen">
      {(() => {
        if (subStep === "alone") {
          return (
            <AloneScreen
              onBack={() => setSubStep("choice")}
              onConfirm={saveInvividualPreferenceForSkorProtect}
              _t={_t}
              redirectLoading={redirectLoading}
            />
          );
        }

        if (subStep === "join-group" && selectedApp) {
          return (
            <div className="isf-container">
              <JoinGroupScreen
                app={selectedApp}
                landlordData={null}
                onBack={null}
                onClose={null}
                onAccept={handleAcceptGroup}
                onDecline={handleDeclineGroup}
                processing={processing}
                user={user}
                _t={_t}
              />
            </div>
          );
        }

        if (subStep === "group") {
          return (
            <CreateGroupScreen
              onBack={() => setSubStep("choice")}
              onClose={() => navigate("/home?welcome=true")}
              onCreateAndShare={handleCreateGroup}
              user={user}
              usedFor={"onboarding"}
            />
          );
        }

        return (
          <ChoiceScreen
            onAlone={() => setSubStep("alone")}
            onGroup={() => setSubStep("group")}
            _t={_t}
            onClose={() => saveInvividualPreferenceForSkorProtect(false)}
          />
        );
      })()}
    </div>
  );
};

export default SkorOnboardingStep;
